import {arktypeResolver} from '@hookform/resolvers/arktype'
import {parseDate} from '@internationalized/date'
import {format} from 'date-fns'
import {dequal} from 'dequal'
import {
  DateField,
  DateInput,
  DateSegment,
  FieldError,
  Form,
  Input,
  Label,
  Radio,
  RadioGroup as RacRadioGroup,
  TextField,
} from 'react-aria-components'
import {Controller, type SubmitHandler, useFieldArray, useForm} from 'react-hook-form'

import CountrySelector from '@/components/CountrySelector/CountrySelector'
import StateProvinceSelector from '@/components/StateProvinceSelector/StateProvinceSelector'
import {isStateProvinceRequiredCountry, PASSENGER_TYPE_MAP} from '@/lib/flight/constants'
import {FlightBooking, type PaxCountry} from '@/lib/flight/flightModel'
import useFlightDetailsQuery from '@/lib/flight/hooks/useFlightDetails'
import {passengersAndContactAtom, useSetPassengersAndContact} from '@/lib/flight/store'
import {Gender, PassengerType} from '@/lib/flight/types'
import {FlightDetailsRoute} from '@/routeRegistry'
import noThrow from '@/utils/schema/noThrow'
import style from '@/views/Flight/FlightDetails.module.scss'

export interface Step3Control {
  submit: () => Promise<FlightBooking>
}

interface Props {
  ref?: RefObject<Step3Control | undefined | null>
  onSubmit: SubmitHandler<FlightBooking>
}

const NoThrowFlightBooking = noThrow(FlightBooking)

export default memo(function FlightDetailsStep3({onSubmit, ref}: Props) {
  const {flightId} = FlightDetailsRoute.useParams()
  const {data: flightDetails} = useFlightDetailsQuery(flightId)
  const setPassengersAndContact = useSetPassengersAndContact()
  const store = useStore()
  const defaultPassengersAndContact = store.get(passengersAndContactAtom)

  const {handleSubmit, control, getValues, trigger, watch, setValue, formState} = useForm({
    mode: 'onTouched',
    resolver: arktypeResolver(NoThrowFlightBooking),
    defaultValues: defaultPassengersAndContact,
  })

  const latestFormState = useLatest(formState)
  const latestGetValues = useLatest(getValues)
  const latestTrigger = useLatest(trigger)

  const {fields, append} = useFieldArray({
    control,
    name: 'passengers', // This matches the array field in `FormValues`
  })

  useEffect(
    function removeNonNumericFromPhone() {
      const subscription = watch((data, {name, type}) => {
        if (type !== 'change' || name !== 'contact.phone') return
        setValue('contact.phone', getValues('contact.phone').replace(/\D/g, ''))
      })

      return () => {
        subscription.unsubscribe()
      }
    },
    [watch, getValues, setValue],
  )

  useEffect(
    function persistDefaultValue() {
      setPassengersAndContact(defaultPassengersAndContact)
    },
    [setPassengersAndContact, defaultPassengersAndContact],
  )

  useEffect(
    function persistData() {
      const subscription = watch(() => {
        const unresolvedValues = getValues()
        try {
          setPassengersAndContact(FlightBooking(unresolvedValues))
        } catch {
          setPassengersAndContact({
            ...unresolvedValues,
            passengers: unresolvedValues.passengers.map(passenger => ({
              ...passenger,
              middleName: passenger.middleName ?? '',
            })),
            contact: {
              ...unresolvedValues.contact,
              middleName: unresolvedValues.contact.middleName ?? '',
            },
          })
        }
      })

      return () => {
        subscription.unsubscribe()
      }
    },
    [watch, setPassengersAndContact, getValues],
  )

  useEffect(
    function addDefaultPassengers() {
      if (flightDetails) {
        const currentPaxTypes = getValues().passengers.map(passenger => passenger.type)
        // Array(flightDetails.metadata.adults).fill(PassengerType.Adult)
        const desiredPaxTypes = [
          ...Array.from({length: flightDetails.metadata.adults}, () => PassengerType.Adult),
          ...Array.from({length: flightDetails.metadata.children}, () => PassengerType.Child),
          ...Array.from(
            {length: flightDetails.metadata.infants_in_seat},
            () => PassengerType.InfantInSeat,
          ),
          ...Array.from(
            {length: flightDetails.metadata.infants_on_lap},
            () => PassengerType.InfantOnLap,
          ),
        ]

        if (dequal(currentPaxTypes, desiredPaxTypes)) return

        // Add default adult passenger based on metadata

        // reset
        setValue('passengers', [])

        // for each adult
        for (let i = 0; i < flightDetails.metadata.adults; i++) {
          append({
            type: PassengerType.Adult,
            lastName: '',
            middleName: '',
            firstName: '',
            gender: Gender.Male,
            dateOfBirth: new Date(),
            country: '' as PaxCountry,
            stateProvince: '',
          })
        }
        // for each child
        for (let i = 0; i < flightDetails.metadata.children; i++) {
          append({
            type: PassengerType.Child,
            lastName: '',
            middleName: '',
            firstName: '',
            dateOfBirth: new Date(),
            gender: Gender.Male,
            country: '' as PaxCountry,
            stateProvince: '',
          })
        }

        for (let i = 0; i < flightDetails.metadata.infants_in_seat; i++) {
          append({
            type: PassengerType.InfantInSeat,
            lastName: '',
            middleName: '',
            firstName: '',
            dateOfBirth: new Date(),
            gender: Gender.Male,
            country: '' as PaxCountry,
            stateProvince: '',
          })
        }

        for (let i = 0; i < flightDetails.metadata.infants_on_lap; i++) {
          append({
            type: PassengerType.InfantOnLap,
            lastName: '',
            middleName: '',
            firstName: '',
            dateOfBirth: new Date(),
            gender: Gender.Male,
            country: '' as PaxCountry,
            stateProvince: '',
          })
        }
      }
    },
    [append, flightDetails, getValues, setValue],
  )

  useEffect(
    function syncFirstAdultInformationToContactInformation() {
      if (!flightDetails) return
      if (flightDetails.metadata.adults === 0) return
      const subscription = watch((value, {name, type}) => {
        if (type === 'change' && name === 'passengers.0.firstName') {
          setValue('contact.firstName', value.passengers?.[0]?.firstName ?? '')
        }

        if (type === 'change' && name === 'passengers.0.middleName') {
          setValue('contact.middleName', value.passengers?.[0]?.middleName ?? '')
        }

        if (type === 'change' && name === 'passengers.0.lastName') {
          setValue('contact.lastName', value.passengers?.[0]?.lastName ?? '')
        }
      })
      return () => {
        subscription.unsubscribe()
      }
    },
    [watch, setValue, flightDetails],
  )

  useImperativeHandle(ref, () => {
    return {
      submit: async () => {
        const result = await latestTrigger.current(undefined, {shouldFocus: true})
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- contains multiple errors
        if (!result) throw latestFormState.current.errors
        const unresolvedValues = latestGetValues.current()
        return FlightBooking(unresolvedValues)
      },
    }
  }, [])

  if (!flightDetails) return null

  return (
    <Form className={style.Form} onSubmit={handleSubmit(onSubmit)}>
      {fields.map((field, index) => {
        const countryCode = watch(`passengers.${index}.country`)
        const shouldDisplayStateProvinceSelector = isStateProvinceRequiredCountry(countryCode)
        return (
          <details key={field.id} open>
            <summary>
              Passenger {index + 1} - {PASSENGER_TYPE_MAP[field.type]}
              {index === 0 && field.type === PassengerType.Adult && ', primary contact'}
            </summary>
            <div className={style.Passenger}>
              <div className={style.PassengerName}>
                <Controller
                  control={control}
                  name={`passengers.${index}.firstName`}
                  render={({
                    field: {name, value, onChange, onBlur, ref},
                    fieldState: {invalid, error},
                  }) => {
                    return (
                      <TextField
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        validationBehavior='aria'
                        isInvalid={invalid}
                        isRequired
                        type='text'
                        autoComplete='given-name'
                        className={style.Field}
                      >
                        <Label className={style.Label}>First name</Label>
                        <Input ref={ref} className={style.Input} />
                        {error?.message && (
                          <FieldError className={style.Error}>{error.message}</FieldError>
                        )}
                      </TextField>
                    )
                  }}
                />
                <Controller
                  control={control}
                  name={`passengers.${index}.middleName`}
                  render={({
                    field: {name, value, onChange, onBlur, ref},
                    fieldState: {invalid, error},
                  }) => {
                    return (
                      <TextField
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        validationBehavior='aria'
                        isInvalid={invalid}
                        isRequired={false}
                        type='text'
                        autoComplete='additional-name'
                        className={style.Field}
                      >
                        <Label className={style.Label}>Middle name (Optional)</Label>
                        <Input ref={ref} className={style.Input} />
                        {error?.message && (
                          <FieldError className={style.Error}>{error.message}</FieldError>
                        )}
                      </TextField>
                    )
                  }}
                />
                <Controller
                  control={control}
                  name={`passengers.${index}.lastName`}
                  render={({
                    field: {name, value, onChange, onBlur, ref},
                    fieldState: {invalid, error},
                  }) => {
                    return (
                      <TextField
                        name={name}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        validationBehavior='aria'
                        isInvalid={invalid}
                        isRequired
                        type='text'
                        autoComplete='family-name'
                        className={style.Field}
                      >
                        <Label className={style.Label}>Last name</Label>
                        <Input ref={ref} className={style.Input} />
                        {error?.message && (
                          <FieldError className={style.Error}>{error.message}</FieldError>
                        )}
                      </TextField>
                    )
                  }}
                />
              </div>
              <Controller
                control={control}
                name={`passengers.${index}.gender`}
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => {
                  return (
                    <RacRadioGroup
                      name={name}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      validationBehavior='aria'
                      isInvalid={invalid}
                      isRequired
                      className={clsx(style.Field, style.Group)}
                      ref={ref}
                    >
                      <Label className={style.Label}>Gender</Label>
                      <div className={style.RadioGroup}>
                        <Radio value={Gender.Male} className={style.Radio}>
                          Male
                        </Radio>
                        <Radio value={Gender.Female} className={style.Radio}>
                          Female
                        </Radio>
                      </div>
                      {error?.message && (
                        <FieldError className={style.Error}>{error.message}</FieldError>
                      )}
                    </RacRadioGroup>
                  )
                }}
              />
              <Controller
                control={control}
                name={`passengers.${index}.dateOfBirth`}
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => {
                  return (
                    <DateField
                      name={name}
                      value={parseDate(format(value, 'yyyy-MM-dd'))}
                      onChange={date => {
                        if (!date) return
                        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
                        const newDate = date.toDate(timezone)
                        onChange(newDate)
                      }}
                      onBlur={onBlur}
                      validationBehavior='aria'
                      isInvalid={invalid}
                      isRequired
                      className={style.Field}
                    >
                      <Label className={style.Label}>Date of birth</Label>
                      <DateInput ref={ref} className={style.Input}>
                        {segment => <DateSegment segment={segment} />}
                      </DateInput>
                      {/* <Text slot="description" /> */}
                      {error?.message && (
                        <FieldError className={style.Error}>{error.message}</FieldError>
                      )}
                    </DateField>
                  )
                }}
              />
              <Controller
                control={control}
                name={`passengers.${index}.country`}
                render={({
                  field: {name, value, onChange, onBlur, ref},
                  fieldState: {invalid, error},
                }) => (
                  <CountrySelector
                    value={value}
                    onChange={onChange}
                    isRequired
                    isInvalid={invalid}
                    errorMessage={error?.message}
                    name={name}
                    onBlur={onBlur}
                    inputRef={ref}
                  />
                )}
              />
              {shouldDisplayStateProvinceSelector && (
                <Controller
                  control={control}
                  name={`passengers.${index}.stateProvince`}
                  render={({
                    field: {name, value, onChange, onBlur, ref},
                    fieldState: {invalid, error},
                  }) => (
                    <StateProvinceSelector
                      value={value}
                      onChange={onChange}
                      countryCode={countryCode}
                      isRequired
                      isInvalid={invalid}
                      errorMessage={error?.message}
                      name={name}
                      onBlur={onBlur}
                      inputRef={ref}
                    />
                  )}
                />
              )}
              {index === 0 && field.type === PassengerType.Adult && (
                <>
                  <Controller
                    control={control}
                    name={`contact.phone`}
                    render={({
                      field: {name, value, onChange, onBlur, ref},
                      fieldState: {invalid, error},
                    }) => {
                      return (
                        <TextField
                          name={name}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          validationBehavior='aria'
                          isInvalid={invalid}
                          isRequired
                          type='tel'
                          autoComplete='tel'
                          className={style.Field}
                        >
                          <Label className={style.Label}>Phone</Label>
                          <Input ref={ref} className={style.Input} />
                          {error?.message && (
                            <FieldError className={style.Error}>{error.message}</FieldError>
                          )}
                        </TextField>
                      )
                    }}
                  />
                  <Controller
                    control={control}
                    name={`contact.email`}
                    render={({
                      field: {name, value, onChange, onBlur, ref},
                      fieldState: {invalid, error},
                    }) => {
                      return (
                        <TextField
                          name={name}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          validationBehavior='aria'
                          isInvalid={invalid}
                          isRequired
                          type='email'
                          autoComplete='email'
                          className={style.Field}
                        >
                          <Label className={style.Label}>Email</Label>
                          <Input ref={ref} className={style.Input} />
                          {error?.message && (
                            <FieldError className={style.Error}>{error.message}</FieldError>
                          )}
                        </TextField>
                      )
                    }}
                  />
                </>
              )}
            </div>
          </details>
        )
      })}
      {flightDetails.metadata.adults === 0 && (
        <div className={style.Contact}>
          <div className={style.Header} role='heading' aria-level={2}>
            Contact information
          </div>
          <Controller
            control={control}
            name={`contact.firstName`}
            render={({
              field: {name, value, onChange, onBlur, ref},
              fieldState: {invalid, error},
            }) => {
              return (
                <TextField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  validationBehavior='aria'
                  isInvalid={invalid}
                  isRequired
                  type='text'
                  autoComplete='given-name'
                  className={style.ContactField}
                >
                  <Label className={style.ContactLabel}>First name</Label>
                  <Input ref={ref} className={style.ContactInput} />
                  {error?.message && (
                    <FieldError className={style.ContactError}>{error.message}</FieldError>
                  )}
                </TextField>
              )
            }}
          />
          <Controller
            control={control}
            name={`contact.middleName`}
            render={({
              field: {name, value, onChange, onBlur, ref},
              fieldState: {invalid, error},
            }) => {
              return (
                <TextField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  validationBehavior='aria'
                  isInvalid={invalid}
                  type='text'
                  autoComplete='additional-name'
                  className={style.ContactField}
                >
                  <Label className={style.ContactLabel}>Middle name (Optional)</Label>
                  <Input ref={ref} className={style.ContactInput} />
                  {error?.message && (
                    <FieldError className={style.ContactError}>{error.message}</FieldError>
                  )}
                </TextField>
              )
            }}
          />
          <Controller
            control={control}
            name={`contact.lastName`}
            render={({
              field: {name, value, onChange, onBlur, ref},
              fieldState: {invalid, error},
            }) => {
              return (
                <TextField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  validationBehavior='aria'
                  isInvalid={invalid}
                  isRequired
                  type='text'
                  autoComplete='family-name'
                  className={style.ContactField}
                >
                  <Label className={style.ContactLabel}>Last name</Label>
                  <Input ref={ref} className={style.ContactInput} />
                  {error?.message && (
                    <FieldError className={style.ContactError}>{error.message}</FieldError>
                  )}
                </TextField>
              )
            }}
          />
          <Controller
            control={control}
            name={`contact.phone`}
            render={({
              field: {name, value, onChange, onBlur, ref},
              fieldState: {invalid, error},
            }) => {
              return (
                <TextField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  validationBehavior='aria'
                  isInvalid={invalid}
                  isRequired
                  type='tel'
                  autoComplete='tel'
                  pattern='[0-9]+'
                  className={style.ContactField}
                >
                  <Label className={style.ContactLabel}>Phone</Label>
                  <Input ref={ref} className={style.ContactInput} />
                  {error?.message && (
                    <FieldError className={style.ContactError}>{error.message}</FieldError>
                  )}
                </TextField>
              )
            }}
          />
          <Controller
            control={control}
            name={`contact.email`}
            render={({
              field: {name, value, onChange, onBlur, ref},
              fieldState: {invalid, error},
            }) => {
              return (
                <TextField
                  name={name}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  validationBehavior='aria'
                  isInvalid={invalid}
                  isRequired
                  type='email'
                  autoComplete='email'
                  className={style.ContactField}
                >
                  <Label className={style.ContactLabel}>Email</Label>
                  <Input ref={ref} className={style.ContactInput} />
                  {error?.message && (
                    <FieldError className={style.ContactError}>{error.message}</FieldError>
                  )}
                </TextField>
              )
            }}
          />
        </div>
      )}
    </Form>
  )
})
